import { useState, useEffect } from 'react';
import { X, Plus, UserMinus, AlertTriangle, Search } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

const EditPreviewGroup = ({ group, allAdolescents, volunteers, maxMembers, onSave, onCancel }) => {
  const [editedGroup, setEditedGroup] = useState({
    ...group,
    leader_ids: group.leader_ids || []
  });
  
  // Search states
  const [leaderSearch, setLeaderSearch] = useState('');
  const [memberSearch, setMemberSearch] = useState('');

  // Filtrar e ordenar líderes disponíveis
  const availableLeaders = volunteers
    .filter(v => v.is_pg_leader && v.status === 'ativo')
    .filter(v => leaderSearch === '' || v.name.toLowerCase().includes(leaderSearch.toLowerCase()))
    .sort((a, b) => a.name.localeCompare(b.name));
  
  // Filtrar e ordenar membros disponíveis para adicionar
  // Exclui apenas inativos - permite ativos e desengajados de qualquer idade
  const availableMembers = allAdolescents
    .filter(a => 
      a.engagement_status !== 'inativo' &&
      !editedGroup.members.find(m => m.id === a.id)
    )
    .filter(a => memberSearch === '' || a.name.toLowerCase().includes(memberSearch.toLowerCase()))
    .sort((a, b) => a.name.localeCompare(b.name));

  // Ordenar membros do grupo alfabeticamente
  const sortedMembers = [...editedGroup.members].sort((a, b) => a.name.localeCompare(b.name));

  const handleAddMember = (adolescent) => {
    setEditedGroup({
      ...editedGroup,
      members: [...editedGroup.members, {
        id: adolescent.id,
        name: adolescent.name,
        age: adolescent.age,
        gender: adolescent.gender
      }]
    });
    setMemberSearch('');
  };

  const handleRemoveMember = (memberId) => {
    setEditedGroup({
      ...editedGroup,
      members: editedGroup.members.filter(m => m.id !== memberId)
    });
  };

  const handleToggleLeader = (leaderId) => {
    const isSelected = editedGroup.leader_ids.includes(leaderId);
    setEditedGroup({
      ...editedGroup,
      leader_ids: isSelected
        ? editedGroup.leader_ids.filter(id => id !== leaderId)
        : [...editedGroup.leader_ids, leaderId]
    });
  };

  const isOverLimit = editedGroup.members.length > maxMembers;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <Label>Nome do Grupo</Label>
        <Input
          value={editedGroup.suggested_name}
          onChange={(e) => setEditedGroup({...editedGroup, suggested_name: e.target.value})}
          className="text-lg font-semibold"
        />
      </div>

      <div className="grid grid-cols-2 gap-4 p-3 bg-gray-50 rounded-lg">
        <div>
          <p className="text-sm text-gray-600">Faixa Etária</p>
          <p className="font-bold">{editedGroup.age_range} anos</p>
        </div>
        <div>
          <p className="text-sm text-gray-600">Gênero</p>
          <p className="font-bold">{editedGroup.gender === 'masculino' ? 'Meninos' : editedGroup.gender === 'feminino' ? 'Meninas' : 'Misto'}</p>
        </div>
      </div>

      {/* Warning if over limit */}
      {isOverLimit && (
        <div className="bg-red-50 border-2 border-red-400 rounded-lg p-3 flex items-start space-x-3">
          <AlertTriangle className="text-red-600 mt-0.5" size={20} />
          <div>
            <p className="font-semibold text-red-900">Limite Excedido!</p>
            <p className="text-sm text-red-700">
              Este grupo tem {editedGroup.members.length} membros, acima do máximo de {maxMembers}. 
              Você pode criar assim, mas considere dividir.
            </p>
          </div>
        </div>
      )}

      {/* Members Count */}
      <div className="flex items-center justify-between p-3 bg-blue-50 rounded-lg">
        <span className="font-semibold text-gray-700">Total de Membros:</span>
        <span className={`text-2xl font-bold ${isOverLimit ? 'text-red-600' : 'text-blue-600'}`}>
          {editedGroup.members.length}
        </span>
      </div>

      {/* Leaders Section */}
      <div>
        <Label className="text-lg font-semibold mb-3 block">Líderes do PG</Label>
        
        {/* Search Leaders */}
        <div className="relative mb-3">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
          <Input
            placeholder="Buscar líder pelo nome..."
            value={leaderSearch}
            onChange={(e) => setLeaderSearch(e.target.value)}
            className="pl-10"
          />
        </div>
        
        {availableLeaders.length > 0 ? (
          <div className="space-y-2 max-h-40 overflow-y-auto">
            {availableLeaders.map((leader) => (
              <div
                key={leader.id}
                className={`flex items-center justify-between p-3 rounded-lg cursor-pointer transition ${
                  editedGroup.leader_ids.includes(leader.id)
                    ? 'bg-purple-100 border-2 border-purple-400'
                    : 'bg-gray-50 hover:bg-gray-100'
                }`}
                onClick={() => handleToggleLeader(leader.id)}
              >
                <div className="flex items-center space-x-3">
                  <input
                    type="checkbox"
                    checked={editedGroup.leader_ids.includes(leader.id)}
                    onChange={() => {}}
                    className="w-5 h-5"
                  />
                  <div>
                    <p className="font-semibold">{leader.name}</p>
                    <p className="text-sm text-gray-600">{leader.phone}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-gray-500 text-center py-4">
            {leaderSearch ? 'Nenhum líder encontrado com esse nome' : 'Nenhum líder de PG disponível'}
          </p>
        )}
      </div>

      {/* Members Section */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <Label className="text-lg font-semibold">Membros do Grupo</Label>
        </div>
        
        {/* Search and Add Members */}
        <div className="relative mb-3">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
          <Input
            placeholder="Buscar adolescente para adicionar..."
            value={memberSearch}
            onChange={(e) => setMemberSearch(e.target.value)}
            className="pl-10"
          />
        </div>
        
        {/* Available members to add */}
        {memberSearch && availableMembers.length > 0 && (
          <div className="mb-4 max-h-40 overflow-y-auto border rounded-lg">
            {availableMembers.slice(0, 10).map((adolescent) => (
              <div
                key={adolescent.id}
                className="flex items-center justify-between p-3 hover:bg-green-50 cursor-pointer border-b last:border-b-0"
                onClick={() => handleAddMember(adolescent)}
              >
                <div>
                  <p className="font-semibold">{adolescent.name}</p>
                  <p className="text-sm text-gray-600">{adolescent.age} anos</p>
                </div>
                <Plus size={18} className="text-green-600" />
              </div>
            ))}
            {availableMembers.length > 10 && (
              <p className="text-center text-gray-500 text-sm py-2">
                + {availableMembers.length - 10} outros resultados...
              </p>
            )}
          </div>
        )}
        
        {memberSearch && availableMembers.length === 0 && (
          <p className="text-gray-500 text-center py-2 mb-3">Nenhum adolescente encontrado</p>
        )}

        {/* Current members list (sorted alphabetically) */}
        <div className="space-y-2 max-h-60 overflow-y-auto">
          {sortedMembers.map((member) => (
            <div
              key={member.id}
              className={`flex items-center justify-between p-3 rounded-lg ${
                isOverLimit ? 'bg-red-50 border border-red-200' : 'bg-gray-50'
              }`}
            >
              <div>
                <p className="font-semibold">{member.name}</p>
                <p className="text-sm text-gray-600">{member.age} anos</p>
              </div>
              <button
                onClick={() => handleRemoveMember(member.id)}
                className="p-2 hover:bg-red-100 rounded-lg transition"
              >
                <UserMinus size={18} className="text-red-600" />
              </button>
            </div>
          ))}
        </div>
      </div>

      {/* Actions */}
      <div className="flex justify-end space-x-3 pt-4 border-t">
        <Button variant="outline" onClick={onCancel}>
          Cancelar
        </Button>
        <Button onClick={() => onSave(editedGroup)} className="btn-primary">
          Salvar Alterações
        </Button>
      </div>
    </div>
  );
};

export default EditPreviewGroup;
